import type {
  AppointmentPlanningManualAppointmentPayload,
  AppointmentPlanningManualFlexSlotOption,
} from "../../../../../../../../../../../../api/kehrblatt/planning"

export type ManualFlexSlotSelectionInput = {
  manualFlexSlotId?: string | null
  appointmentEmployeeId?: string | null
  manualAppointment?: AppointmentPlanningManualAppointmentPayload | null
  appointmentStartAt?: string | null
  appointmentEndAt?: string | null
  appointmentTimeframeStartAt?: string | null
  appointmentTimeframeEndAt?: string | null
  options: AppointmentPlanningManualFlexSlotOption[]
}

const normalizeText = (value?: string | null): string => String(value ?? "").trim()

const instantMs = (value?: string | null): number | null => {
  if (!value) return null

  const timestamp = Date.parse(value)

  return Number.isFinite(timestamp) ? timestamp : null
}

const sameInstant = (left?: string | null, right?: string | null): boolean => {
  const leftMs = instantMs(left)
  const rightMs = instantMs(right)

  return leftMs !== null && rightMs !== null && leftMs === rightMs
}

const exactAppointmentMatch = (
  option: AppointmentPlanningManualFlexSlotOption,
  appointmentStartAt?: string | null,
  appointmentEndAt?: string | null,
): boolean =>
  sameInstant(option.appointment_start_at, appointmentStartAt) &&
  sameInstant(option.appointment_end_at, appointmentEndAt)

export const findSelectedManualFlexSlot = ({
  manualFlexSlotId,
  appointmentEmployeeId,
  manualAppointment,
  appointmentStartAt,
  appointmentEndAt,
  appointmentTimeframeStartAt,
  appointmentTimeframeEndAt,
  options,
}: ManualFlexSlotSelectionInput): AppointmentPlanningManualFlexSlotOption | null => {
  const slotId = normalizeText(manualFlexSlotId)

  if (slotId) return options.find((option) => option.slot_id === slotId) ?? null

  const employeeId = normalizeText(appointmentEmployeeId)
  const flexAppointment = manualAppointment?.flex_type === "SOFT" ? manualAppointment : null
  const timeframeStartAt =
    flexAppointment?.timeframe_start_at ??
    appointmentTimeframeStartAt ??
    flexAppointment?.start_at ??
    appointmentStartAt
  const timeframeEndAt =
    flexAppointment?.timeframe_end_at ??
    appointmentTimeframeEndAt ??
    flexAppointment?.end_at ??
    appointmentEndAt

  if (!timeframeStartAt || !timeframeEndAt) return null

  const timeframeMatches = options.filter((option) => {
    if (employeeId && String(option.employee_id) !== employeeId) return false

    return (
      sameInstant(option.timeframe_start_at, timeframeStartAt) &&
      sameInstant(option.timeframe_end_at, timeframeEndAt)
    )
  })

  if (timeframeMatches.length === 1) return timeframeMatches[0]

  const effectiveAppointmentStartAt = flexAppointment?.start_at ?? appointmentStartAt
  const effectiveAppointmentEndAt = flexAppointment?.end_at ?? appointmentEndAt
  const exactMatches = timeframeMatches.filter((option) =>
    exactAppointmentMatch(option, effectiveAppointmentStartAt, effectiveAppointmentEndAt),
  )

  return exactMatches.length === 1 ? exactMatches[0] : null
}

export const manualFlexSlotValue = (input: ManualFlexSlotSelectionInput): string =>
  findSelectedManualFlexSlot(input)?.slot_id ?? normalizeText(input.manualFlexSlotId)
